import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  cn,
  Modal,
  ModalActions,
  ModalContent,
  ModalScreenReaderTitle,
  ModalTitleInfo,
} from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import useGroupByBuyer, { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import { useReporting } from '@/reporting';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useExchangeRates from '@/shared/data/useExchangeRates';
import getBuyerStats from '@/utils/getBuyerStats';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useIsNewDivision from '@/utils/useIsNewDivision';
import DivisionDetailsChips, { getHasActiveOffer, useDivisionDetailsChips } from '../DivisionDetailsChips';
import EditInvoiceCriteriaModal from './EditInvoiceCriteriaModal';
import { useEditInvoicesContext } from './EditInvoicesContext';
import EditInvoicesHeader from './EditInvoicesHeader';
import EditInvoicesLayout from './EditInvoicesLayout';
import { EditInvoicesSidebarDesktop, EditInvoicesSidebarMobile } from './EditInvoicesSidebar';
import InvoiceEditLoadingModal from './InvoiceEditLoadingModal';
import useInvoiceEdits from './useInvoiceEdits';

export type EditInvoicesReferrer = 'division-details' | 'offer-builder' | 'offer-review';

export interface EditInvoicesProps {
  asModal?: boolean;
  buyers?: BuyerGroupWithStats[];
  onCloseInvoiceModal?: () => void;
  readOnly?: boolean;
  referrer: EditInvoicesReferrer;
  showInvoiceModal?: boolean;
  takerMarket?: TakerMarket | null;
  takerMarkets?: TakerMarket[];
}

const EditInvoicesAlert = ({ takerMarket }: { takerMarket: TakerMarket }) => {
  const { t } = useTranslation();
  const { hasActiveOffer, recurringRules } = useDivisionDetailsChips(takerMarket);
  const hasInvoiceRules = recurringRules.length > 0;

  if (!hasActiveOffer && !hasInvoiceRules) {
    return null;
  }

  let description: string;

  if (hasActiveOffer && hasInvoiceRules) {
    description = t('invoices.editInvoicesDisabledBoth');
  } else if (hasActiveOffer) {
    description = t('invoices.editInvoicesDisabledActiveOffer');
  } else {
    description = t('invoices.editInvoicesDisabledInvoiceRules');
  }

  return <Alert description={description} full variant="outlined" />;
};

const EditInvoices = ({
  asModal = true,
  buyers: buyersProp,
  onCloseInvoiceModal,
  readOnly = false,
  referrer,
  showInvoiceModal,
  takerMarket,
  takerMarkets,
}: EditInvoicesProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const { isNewDivision } = useIsNewDivision();
  const { dispatch, state } = useEditInvoicesContext();
  const { loading, progress, errors } = state;
  const [criteriaModal, setCriteriaModal] = useState<{
    takerMarkets: TakerMarket[];
    referrer: 'customer-select' | 'division-details';
  } | null>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [pendingAction, setPendingAction] = useState<
    { type: 'close' } | { type: 'switchTakerMarket'; tm: TakerMarket } | null
  >(null);

  // When takerMarkets array is provided, manage active selection internally.
  // Falls back to takerMarkets[0] so resolvedTakerMarket stays current when the prop changes.
  const [activeTakerMarket, setActiveTakerMarket] = useState<TakerMarket | null>(null);
  const hasSelectedInvoices = state.selected.length > 0 || state.selectionMode === 'all';

  useEffect(() => {
    if (showInvoiceModal) {
      setActiveTakerMarket(null);
    }
  }, [showInvoiceModal]);

  const filteredTakerMarkets = takerMarkets?.filter((tm) => tm.totalInvoiceAmount > 0);
  const filteredTakerMarketIds = filteredTakerMarkets ? new Set(filteredTakerMarkets.map((tm) => tm.id)) : null;
  const fallbackBuyers = useGroupByBuyer({ takerMarkets: filteredTakerMarkets ?? [], sortByEligible: true });
  const buyers = (buyersProp ?? fallbackBuyers)
    .map((buyer) => {
      const filteredBuyerTakerMarkets = buyer.takerMarkets.filter(
        (tm) => tm.totalInvoiceAmount > 0 && (!filteredTakerMarketIds || filteredTakerMarketIds.has(tm.id))
      );
      return {
        ...buyer,
        takerMarkets: filteredBuyerTakerMarkets,
        stats: getBuyerStats({
          buyer: { ...buyer, takerMarkets: filteredBuyerTakerMarkets },
          exchangeRates,
          isNewDivision,
          selectedCurrency,
          includeAwardedInvoices: true,
        }),
      };
    })
    .filter((buyer) => buyer.takerMarkets.length > 0);
  const firstSortedTakerMarket = buyers[0]?.takerMarkets[0] ?? null;
  const resolvedTakerMarket = filteredTakerMarkets ? activeTakerMarket ?? firstSortedTakerMarket : takerMarket ?? null;
  const showSidebar = !!filteredTakerMarkets && filteredTakerMarkets.length > 1;

  const headerStats = buyers.reduce(
    (acc, buyer) => ({
      eligibleInvoiceAmount: acc.eligibleInvoiceAmount + buyer.stats.eligibleInvoiceAmount,
      eligibleInvoiceCount: acc.eligibleInvoiceCount + buyer.stats.eligibleInvoiceCount,
      totalInvoiceCount: acc.totalInvoiceCount + buyer.stats.totalInvoiceCount,
    }),
    { eligibleInvoiceAmount: 0, eligibleInvoiceCount: 0, totalInvoiceCount: 0 }
  );
  const uniqueCurrencyCount = new Set(filteredTakerMarkets?.map((tm) => tm.currency)).size;
  const uniqueBuyerCount = buyers.length;

  // when asModal is true, its in the offer builder modal and we don't need to subscribe to market stats
  const subscribeToMarketStats = asModal === false;

  const { handleIncludeByCriteria } = useInvoiceEdits({
    onLoadingChange: (loading) => dispatch({ type: 'SET_LOADING', payload: { loading } }),
    onProgressChange: (current, total) => dispatch({ type: 'SET_PROGRESS', payload: { current, total } }),
    onResetSelection: () => dispatch({ type: 'RESET_SELECTED' }),
    getSelectedInvoices: () => state.selected,
    onError: (errors) => dispatch({ type: 'SET_ERRORS', payload: { errors } }),
    subscribeToMarketStats,
  });
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const bulkEditTakerMarkets = filteredTakerMarkets ?? (resolvedTakerMarket ? [resolvedTakerMarket] : []);
  const canBulkEditInvoices = bulkEditTakerMarkets.some(
    (tm) => !getHasActiveOffer(tm) && getRecurringRulesForTakerMarkets([tm]).length === 0
  );

  if (!resolvedTakerMarket) {
    return null;
  }

  const resolvedTakerMarketAttrs = {
    makerOrgUuid: resolvedTakerMarket.makerOrganizationUuid,
    marketUuid: resolvedTakerMarket.marketUuid,
    takerUuid: resolvedTakerMarket.takerDivisionUuid,
    referrer,
    selectedCount: state.selectionMode === 'all' ? 'all' : state.selected.length,
  };

  const guardedClose = () => {
    if (pendingAction) {
      return;
    }

    if (hasSelectedInvoices) {
      trackEnterpriseEvent('invoices::unsaved-changes-modal::shown', resolvedTakerMarketAttrs);
      setPendingAction({ type: 'close' });
    } else {
      onCloseInvoiceModal?.();
    }
  };

  const guardedSetActiveTakerMarket = (tm: TakerMarket) => {
    if (hasSelectedInvoices) {
      trackEnterpriseEvent('invoices::unsaved-changes-modal::shown', resolvedTakerMarketAttrs);
      setPendingAction({ type: 'switchTakerMarket', tm });
    } else {
      trackEnterpriseEvent('invoices::customer-switched', {
        makerOrgUuid: tm.makerOrganizationUuid,
        marketUuid: tm.marketUuid,
        takerUuid: tm.takerDivisionUuid,
        referrer,
      });
      setActiveTakerMarket(tm);
    }
  };

  const confirmPendingAction = () => {
    if (!pendingAction) {
      return;
    }

    // resolvedTakerMarket is guaranteed non-null here: pendingAction can only be set after the
    // early return guard confirms resolvedTakerMarket is present.
    const tm = pendingAction.type === 'switchTakerMarket' ? pendingAction.tm : resolvedTakerMarket;

    trackEnterpriseEvent('invoices::unsaved-changes-modal::continued', {
      makerOrgUuid: tm.makerOrganizationUuid,
      marketUuid: tm.marketUuid,
      takerUuid: tm.takerDivisionUuid,
      referrer,
    });

    dispatch({ type: 'RESET_SELECTED' });

    if (pendingAction.type === 'close') {
      onCloseInvoiceModal?.();
    } else {
      trackEnterpriseEvent('invoices::customer-switched', {
        makerOrgUuid: pendingAction.tm.makerOrganizationUuid,
        marketUuid: pendingAction.tm.marketUuid,
        takerUuid: pendingAction.tm.takerDivisionUuid,
        referrer,
      });
      setActiveTakerMarket(pendingAction.tm);
    }

    setPendingAction(null);
  };

  const content = (
    <QueryBoundaries>
      <InvoiceEditLoadingModal
        open={loading}
        progress={progress}
        errors={errors}
        onClearErrors={() => dispatch({ type: 'CLEAR_ERRORS' })}
        onClose={() => dispatch({ type: 'SET_LOADING', payload: { loading: false } })}
      />
      <EditInvoiceCriteriaModal
        onClose={() => setCriteriaModal(null)}
        onApply={(criteria) => handleIncludeByCriteria(criteria)}
        open={criteriaModal !== null}
        referrer={criteriaModal?.referrer ?? 'division-details'}
        takerMarkets={criteriaModal?.takerMarkets ?? [resolvedTakerMarket]}
      />
      {asModal && (
        <EditInvoicesHeader
          headerStats={headerStats}
          onDone={guardedClose}
          readOnly={readOnly}
          referrer={referrer}
          resolvedTakerMarket={resolvedTakerMarket}
          selectedCurrency={selectedCurrency}
          uniqueBuyerCount={uniqueBuyerCount}
          uniqueCurrencyCount={uniqueCurrencyCount}
        />
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
        {showSidebar && (
          <EditInvoicesSidebarDesktop
            buyers={buyers}
            onSelectTakerMarket={guardedSetActiveTakerMarket}
            resolvedTakerMarket={resolvedTakerMarket}
            selectedCurrency={selectedCurrency}
          />
        )}
        {/* scrollContainer: on mobile wraps everything including headers; on desktop just the main content pane */}
        <div ref={setScrollContainer} className={cn('min-w-0 flex-1 overflow-y-auto', { 'p-4 md:p-4': asModal })}>
          {asModal && (
            <>
              {/* bulk actions — scrolls on mobile, stays in flow on desktop */}
              {!readOnly && canBulkEditInvoices && (
                <div className="mb-4 flex flex-col justify-between gap-x-4 gap-y-2 rounded-xl bg-white p-4 shadow md:flex-row md:items-center md:p-6">
                  <div className="text-lg font-medium">{t('core.editAllInvoiceActions')}</div>
                  <div>
                    <Button
                      className="w-full md:w-auto"
                      onClick={() =>
                        setCriteriaModal({
                          takerMarkets: filteredTakerMarkets ?? [resolvedTakerMarket],
                          referrer: 'customer-select',
                        })
                      }
                      variant="tertiary"
                    >
                      {t('invoices.includeByCriteriaTitle')}
                    </Button>
                  </div>
                </div>
              )}
              {showSidebar && (
                <EditInvoicesSidebarMobile
                  buyers={buyers}
                  onSelectTakerMarket={guardedSetActiveTakerMarket}
                  resolvedTakerMarket={resolvedTakerMarket}
                  selectedCurrency={selectedCurrency}
                />
              )}
            </>
          )}
          <div className={cn({ 'rounded-xl bg-white p-4 shadow': asModal })}>
            {asModal && (
              <div className="pb-6">
                <div className="text-lg font-medium">
                  {resolvedTakerMarket?.makerOrganizationName} | {getTakerMarketName(resolvedTakerMarket)}{' '}
                  {t('maker.invoices')}
                </div>
                <div className="space-y-4">
                  <DivisionDetailsChips displayTooltip={false} size="xs" takerMarket={resolvedTakerMarket} />
                  {!readOnly && <EditInvoicesAlert takerMarket={resolvedTakerMarket} />}
                </div>
              </div>
            )}
            <EditInvoicesLayout
              asModal={asModal}
              hasSidebar={showSidebar}
              readOnly={readOnly}
              referrer={referrer}
              scrollContainer={scrollContainer}
              setShowInvoiceCriteriaModal={() =>
                setCriteriaModal({ takerMarkets: [resolvedTakerMarket], referrer: 'division-details' })
              }
              subscribeToMarketStats={subscribeToMarketStats}
              takerMarket={resolvedTakerMarket}
            />
          </div>
        </div>
      </div>
    </QueryBoundaries>
  );

  return asModal ? (
    <>
      <Modal
        onClose={() => {
          trackEnterpriseEvent('invoices::unsaved-changes-modal::cancelled', resolvedTakerMarketAttrs);
          setPendingAction(null);
        }}
        open={pendingAction !== null}
        size="sm"
      >
        <ModalTitleInfo>{t('invoices.unsavedChangesTitle')}</ModalTitleInfo>
        <ModalContent>
          <p>{t('invoices.unsavedChangesDescription')}</p>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() => {
              trackEnterpriseEvent('invoices::unsaved-changes-modal::cancelled', resolvedTakerMarketAttrs);
              setPendingAction(null);
            }}
            variant="ancillary"
          >
            {t('core.cancel')}
          </Button>
          <Button onClick={confirmPendingAction} variant="primary">
            {t('core.continue')}
          </Button>
        </ModalActions>
      </Modal>
      <Modal
        className={cn(
          'min-h-[calc(100vh-1rem)] min-w-[calc(100vw-1rem)] overflow-hidden data-[state=open]:animate-[slide-up_0.2s_ease-out_forwards]',
          { 'bg-canvas': asModal }
        )}
        onClose={guardedClose}
        open={showInvoiceModal}
      >
        <ModalScreenReaderTitle>{t(readOnly ? 'core.viewInvoices' : 'core.editInvoices')}</ModalScreenReaderTitle>
        <ModalContent className="p-0">
          <div className="flex h-[calc(100vh-1rem)] flex-col overflow-hidden">{content}</div>
        </ModalContent>
      </Modal>
    </>
  ) : (
    content
  );
};

export default EditInvoices;
